/**
 * PostCard Component
 *
 * Diamond Architecture: Dumb client component for blog post cards.
 * - Works everywhere (carousel, grid, search results)
 * - Uses plain types from mappers (Unicorn Standard)
 * - Compact variant for carousels, full for grids
 */
import { Link } from '@/shared/i18n/routing';
import { useLocale } from '@/shared/i18n/useTranslations';
import type { PostCardData } from '@/shared/lib/types/content';
import { formatDate } from '@letsgethome/lib/utils/timestamp';
import { routes } from '@letsgethome/routes';
import { plainText, plaintextString } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { S3Image } from '@letsgethome/ui/s3-image';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

export type PostCardVariant = 'full' | 'compact';

interface PostCardProps {
  post: PostCardData;
  variant?: PostCardVariant;
  /** Responsive sizes attribute for next/image srcset selection */
  sizes: string;
}

export function PostCard({ post, variant = 'full', sizes }: PostCardProps) {
  const locale = useLocale();
  const isCompact = variant === 'compact';

  return (
    <Link
      href={routes.blog.post(post.slug)}
      prefetch="intent"
      className="group flex flex-col h-full"
    >
      {/* Image - rounded corners (matches ListingCard) */}
      <div
        className={`relative overflow-hidden rounded-lg ${isCompact ? 'aspect-[16/10]' : 'aspect-[16/10]'}`}
      >
        {post.imageKey ? (
          <>
            {/* Blurred background image for contain-fit posts (Decoman hack) */}
            {post.useContainFit && (
              <S3Image
                src={post.imageKey}
                decorative
                fill
                className={cn('object-cover blur-lg scale-105', Z_LAYERS.mediaBackdrop.className)}
                aria-hidden="true"
                sizes={sizes}
              />
            )}
            {/* Main image */}
            <S3Image
              src={post.imageKey}
              alt={plainText(post.title)}
              fill
              className={cn(
                post.useContainFit
                  ? ['object-contain', Z_LAYERS.mediaForeground.className]
                  : 'object-cover',
                'group-hover:scale-105 transition-transform duration-300'
              )}
              sizes={sizes}
            />
          </>
        ) : (
          <div className="w-full h-full bg-muted" />
        )}
        {/* Partner logo overlay (top-left) - skip if useContainFit since branding is in image.
            The logo IDENTIFIES the partner (content, not decoration) — alt is
            the partner name (R4 PR-4; partner names are not localized). */}
        {post.partnerLogoHorizontalKey && !post.useContainFit && (
          <div className={cn('absolute top-0 left-0', Z_LAYERS.cardControls.className)}>
            <S3Image
              src={post.partnerLogoHorizontalKey}
              alt={plainText(post.partnerName)}
              width={80}
              height={36}
              className="h-9 w-auto rounded-br-lg"
            />
          </div>
        )}
      </div>

      {/* Content - no horizontal padding (matches ListingCard) */}
      <div className={`flex-1 flex flex-col ${isCompact ? 'pt-2' : 'pt-3'}`}>
        <h2
          className={`font-semibold text-foreground line-clamp-2 ${
            isCompact ? 'text-base' : 'text-lg'
          }`}
        >
          {plaintextString(post.title)}
        </h2>
        {!isCompact && (
          <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
            {plaintextString(post.summary)}
          </p>
        )}
        <time className="mt-auto pt-3 block text-xs text-muted-foreground" suppressHydrationWarning>
          {formatDate(post.publishedAt, locale)}
        </time>
      </div>
    </Link>
  );
}
